import { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/layout";
import {
  useCreateSession,
  useListPlaybooks,
  useStartPlaybook,
  useDeleteCustomPlaybook,
} from "@workspace/api-client-react";
import type { Playbook } from "@workspace/api-client-react";
import { useAuth } from "@/hooks/useAuth";
import { useTrialExhaustedDialog } from "@/hooks/use-trial-exhausted-dialog";
import { isTrialExhausted } from "@/lib/trial";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Link2,
  Mic,
  MicOff,
  Type,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import { Waveform } from "@/components/waveform";
import { clearPendingInput, readPendingInput } from "@/lib/pending-input";
import { sortPlaybooksByAddedAt } from "@/lib/playbook-utils";
import { pushRecentPlaybookId } from "@/lib/recent-playbooks";
import { getLinkPreview } from "@/lib/link-preview";
import { LinkPreviewCard } from "@/components/link-preview-card";
import { PlaybookSwimLane } from "@/components/playbook-swim-lane";
import { PlaybookPreviewSheet, CARD_BACKGROUNDS, type CardBg } from "@/components/playbook-preview-sheet";
import { WaitingThoughtCard, useWaitingThought } from "@/components/assistant/waiting-thought";
import { HandsGalaxyVideo } from "@/components/hands-galaxy-video";

export default function HomeScreen() {
  const [, setLocation] = useLocation();
  const { user, refetch } = useAuth();
  const queryClient = useQueryClient();
  const { data: playbooks } = useListPlaybooks();
  const [inputType, setInputType] = useState<"link" | "text">("link");
  const [inputValue, setInputValue] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Playbook | null>(null);
  const [selectedCardBg, setSelectedCardBg] = useState<CardBg | null>(null);
  const { trialExhaustedDialog, showTrialExhaustedDialog } = useTrialExhaustedDialog();
  const trialBlocked = isTrialExhausted(user);
  const waitingThought = useWaitingThought();
  const [heroFocused, setHeroFocused] = useState(false);

  const { mutate: createSession, isPending: isCreatingSession } = useCreateSession({
    mutation: {
      onSuccess: (data) => {
        setSubmitError(null);
        void refetch();
        setLocation(`/processing/${data.id}`);
      },
      onError: (err: unknown) => {
        if (showTrialExhaustedDialog(err)) {
          setSubmitError(null);
          void refetch();
          return;
        }
        setSubmitError("Something went wrong. Please try again.");
      },
    },
  });

  const { mutate: startPlaybook, isPending: isStarting } = useStartPlaybook({
    mutation: {
      onSuccess: (data) => {
        setStartError(null);
        setLocation(`/processing/${data.id}`);
      },
      onError: (err: unknown) => {
        if (showTrialExhaustedDialog(err)) {
          setStartError(null);
          void refetch();
          return;
        }
        setStartError("Something went wrong. Please try again.");
      },
    },
  });

  const { mutate: deletePlaybook } = useDeleteCustomPlaybook({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["listPlaybooks"] });
        setSelected(null);
      },
    },
  });

  const voice = useVoiceInput({
    onTranscript: (text) => setInputValue((p) => (p ? p + " " + text : text)),
  });

  useEffect(() => {
    const pending = readPendingInput();
    if (pending) {
      setInputType(pending.type);
      setInputValue(pending.value);
      clearPendingInput();
    }
  }, []);

  const handleInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isCreatingSession || trialBlocked) return;
    if (voice.isListening) voice.stop();
    createSession({ data: { input: inputValue, type: inputType } });
  };

  /** Opens the preview sheet — matches the Library page's behavior of previewing
   * a trigger before committing to a session, rather than starting immediately. */
  const handleCardClick = (playbook: Playbook, bg: CardBg) => {
    setSelected(playbook);
    setSelectedCardBg(bg);
  };

  const handleStartPlaybook = () => {
    if (!selected || isStarting || trialBlocked) return;
    pushRecentPlaybookId(selected.id);
    startPlaybook({ id: selected.id });
  };

  const linkPreview = inputType === "link" ? getLinkPreview(inputValue) : null;
  const allPlaybooks = sortPlaybooksByAddedAt(playbooks ?? []);

  const createdPlaybooks = useMemo(
    () => allPlaybooks.filter((p) => p.isCustom && p.canDelete),
    [allPlaybooks],
  );

  const libraryPlaybooks = useMemo(() => allPlaybooks, [allPlaybooks]);

  return (
    <Layout>
      {trialExhaustedDialog}
      <div className="relative pb-16">
        {/* Hero: night panel with the hands-galaxy banner; the hands sit around the input. */}
        <section
          aria-labelledby="home-hero-title"
          className="relative mx-3 mt-3 overflow-hidden rounded-[24px] bg-night shadow-[0_30px_70px_rgba(40,30,140,0.28)] sm:mx-8 sm:mt-5 md:rounded-[32px] lg:min-h-[600px] min-[1440px]:mx-auto min-[1440px]:max-w-[1376px]"
        >
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <HandsGalaxyVideo
              className={cn(
                "absolute inset-0 h-full w-full transition-transform duration-[1400ms] ease-out motion-reduce:transition-none",
                heroFocused && "scale-[1.09]",
              )}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[rgba(10,9,34,0.72)] to-[rgba(10,9,34,0)] to-[62%]" />
            <div
              className="absolute left-1/2 top-[-70px] h-[420px] w-[1120px] max-w-[160%] -translate-x-1/2"
              style={{ background: "radial-gradient(50% 50% at 50% 50%, rgba(10,9,34,0.78), rgba(10,9,34,0))" }}
            />
            <div
              className={cn(
                "absolute left-1/2 top-[163px] h-[464px] w-[1068px] max-w-[160%] -translate-x-1/2 transition-[transform,opacity] duration-700 ease-out motion-reduce:transition-none",
                heroFocused ? "scale-x-125 scale-y-[1.35] opacity-100" : "opacity-90",
              )}
              style={{ background: "radial-gradient(50% 50% at 50% 50%, rgba(184,166,255,0.55), rgba(184,166,255,0))" }}
            />
          </div>

          <div className="relative flex flex-col items-center px-4 pb-12 pt-10 sm:px-8 md:pb-[83px] md:pt-11">
            <header className="mx-auto mb-8 w-full max-w-3xl text-center md:mb-10">
              <h1
                id="home-hero-title"
                className="font-display text-[34px] font-semibold leading-[1.1] tracking-[-0.04em] text-starlight sm:text-5xl md:text-[52px]"
              >
                What will you unfold today
                {user?.firstName ? (
                  <>, <span className="font-bold text-lilac-light">{user.firstName}</span></>
                ) : null}
                ?
              </h1>
              <p className="mx-auto mt-4 max-w-[511px] text-base leading-relaxed text-[#d2ceea] md:text-lg md:leading-[29.3px]">
                Drop a link or a thought — Unfold turns what you consume into reflection and momentum.
              </p>
            </header>

            <div className="mx-auto w-full max-w-[768px] space-y-4">
          {trialBlocked && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 px-4 py-3 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-sm text-center space-y-2"
            >
              <p>You&apos;ve used all {user?.trialEntriesLimit ?? 5} Starter Unfoldings.</p>
              <button
                type="button"
                onClick={() => setLocation("/pricing")}
                className="inline-block font-semibold text-primary hover:underline"
              >
                View plans to keep unfolding
              </button>
            </motion.div>
          )}
          {submitError && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 px-4 py-3 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-sm text-center"
            >
              {submitError}
            </motion.div>
          )}
          {startError && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 px-4 py-3 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-sm text-center"
            >
              {startError}
            </motion.div>
          )}

          {/* A thought an AI assistant sent to unfold later takes the input's place until it's handled. */}
          {waitingThought && <WaitingThoughtCard thought={waitingThought.thought} more={waitingThought.more} />}

          {/* Input card: on focus the hands ease apart and the glow behind it brightens (Figma 04b). */}
          <motion.form
            hidden={Boolean(waitingThought)}
            onSubmit={handleInputSubmit}
            onFocus={() => setHeroFocused(true)}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setHeroFocused(false);
            }}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className={cn(
              "overflow-hidden rounded-[24px] bg-white transition-[box-shadow,border-color] duration-500",
              heroFocused
                ? "border-2 border-[#b8a6ff] shadow-[0_0_70px_rgba(184,166,255,0.85),0_24px_60px_rgba(10,9,34,0.45)]"
                : "border-2 border-white/60 shadow-[0_0_48px_rgba(184,166,255,0.45),0_24px_60px_rgba(10,9,34,0.45)]",
            )}
          >
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              aria-label={inputType === "link" ? "Link to unfold" : "Thought to unfold"}
              placeholder={
                inputType === "link"
                  ? "Paste a YouTube, Instagram, TikTok, or article URL…"
                  : "Write whatever's on your mind right now…"
              }
              className="w-full min-h-[132px] md:min-h-[160px] bg-transparent px-5 md:px-6 pt-5 md:pt-6 pb-2 text-lg md:text-xl leading-7 text-foreground placeholder:text-muted-foreground/50 focus:outline-none resize-none"
              disabled={isCreatingSession || trialBlocked}
            />

            {linkPreview && (
              <div className="px-4 pb-2">
                <LinkPreviewCard
                  preview={linkPreview}
                  href={inputValue.trim()}
                  onClear={() => setInputValue("")}
                />
              </div>
            )}

            {voice.isListening && (
              <div className="flex items-center gap-3 px-5 py-2.5 bg-primary/5 border-t border-primary/10">
                <Waveform active className="h-5" />
                <span className="text-sm text-primary/70 animate-pulse font-medium">Listening…</span>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 border-t-[0.6px] border-foreground/5 bg-[rgba(241,238,233,0.2)] px-4 py-3 md:px-5 md:py-4">
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => { setInputType("link"); voice.isListening && voice.stop(); }}
                  className={cn(
                    "flex items-center gap-1.5 rounded-[14.5px] px-3 py-1.5 text-xs font-medium transition-all",
                    inputType === "link"
                      ? "border-[0.6px] border-foreground/5 bg-white text-foreground shadow-[0_1px_1px_rgba(47,41,37,0.1),0_1px_1.5px_rgba(47,41,37,0.1)]"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <Link2 className="w-3.5 h-3.5" /> Link
                </button>
                <button
                  type="button"
                  onClick={() => { setInputType("text"); voice.isListening && voice.stop(); }}
                  className={cn(
                    "flex items-center gap-1.5 rounded-[14.5px] px-3 py-1.5 text-xs font-medium transition-all",
                    inputType === "text"
                      ? "border-[0.6px] border-foreground/5 bg-white text-foreground shadow-[0_1px_1px_rgba(47,41,37,0.1),0_1px_1.5px_rgba(47,41,37,0.1)]"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <Type className="w-3.5 h-3.5" /> Text
                </button>
                {inputType === "text" && voice.isSupported && (
                  <button
                    type="button"
                    onClick={voice.toggle}
                    className={cn(
                      "flex items-center gap-1 rounded-[14.5px] px-2.5 py-1.5 text-xs font-medium transition-all",
                      voice.isListening
                        ? "bg-primary text-white"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                    title={voice.isListening ? "Stop" : "Speak"}
                  >
                    {voice.isListening ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>

              <button
                type="submit"
                disabled={!inputValue.trim() || isCreatingSession || trialBlocked}
                className={cn(
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-[0_6px_10px_rgba(110,83,172,0.45)] transition-all hover:bg-primary/90 disabled:opacity-35 md:h-11 md:w-11",
                  heroFocused && "disabled:opacity-60",
                )}
                title={isCreatingSession ? "Starting…" : "Unfold this"}
                aria-label="Unfold this"
              >
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </motion.form>
            </div>
          </div>
        </section>

        <div className="relative flex flex-col items-center px-5">
          {/* Library section */}
          <div className="w-full max-w-7xl mx-auto mt-10 md:mt-[37px]">
            <p className="eyebrow mb-4 text-center">
              Not sure where to start? Browse the library
            </p>

            {/* Swim lanes — Library (newest first) + Created by you */}
            <PlaybookSwimLane
              title="Library"
              playbooks={libraryPlaybooks}
              selectedId={selected?.id}
              onCardClick={(playbook, idx) =>
                handleCardClick(playbook, CARD_BACKGROUNDS[idx % CARD_BACKGROUNDS.length]!)
              }
              onViewAll={() => setLocation("/library")}
            />
            <PlaybookSwimLane
              title="Created by you"
              playbooks={createdPlaybooks}
              selectedId={selected?.id}
              onCardClick={(playbook, idx) =>
                handleCardClick(playbook, CARD_BACKGROUNDS[idx % CARD_BACKGROUNDS.length]!)
              }
              onViewAll={() => setLocation("/library")}
            />
            {libraryPlaybooks.length === 0 && (
              <div className="rounded-3xl border border-dashed border-border bg-muted/30 px-6 py-14 text-center">
                <p className="text-muted-foreground text-sm">
                  Nothing in the library yet — start one with the box above.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <PlaybookPreviewSheet
        playbook={selected}
        cardBg={selectedCardBg}
        onClose={() => setSelected(null)}
        onStart={handleStartPlaybook}
        isStarting={isStarting}
        startError={startError ?? undefined}
        onDelete={selected ? () => deletePlaybook({ id: selected.id }) : undefined}
      />
    </Layout>
  );
}
